import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { State } from '@app/models';
import { AuthEntity } from '../auth-base.model';
import { AuthBaseApi } from '../auth-base.api';
import { RefreshTokenLocalSuccess } from '../auth-base.action';
import { getStoredAuth, updateStoredTokens } from '../auth-base.utils';

/**
 * Refresh a bit before the real expiration to cover clock skew / latency.
 */
const REFRESH_BUFFER_MS = 30 * 1000;

@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {

  private _inFlight$: Observable<AuthEntity>;

  constructor(
    private _store: Store<State>,
    private _authApi: AuthBaseApi
  ) { }

  /**
   * Guarantees the store & localStorage hold a non-expired access token,
   * calling `refresh$` only when the current token is missing or about to
   * expire. Concurrent callers share a single in-flight refresh request.
   */
  ensureFreshToken$(): Observable<AuthEntity> {
    const stored = getStoredAuth();

    if (!stored || !stored.auth || !stored.auth.refreshToken) {
      return throwError(() => new Error('No stored refresh token available.'));
    }

    if (this._isTokenValid(stored.auth.expiresAt)) {
      return of(stored.auth);
    }

    if (!this._inFlight$) {
      this._inFlight$ = this._authApi
        .refresh$(stored.auth.refreshToken)
        .pipe(
          tap((auth) => {
            updateStoredTokens(auth);
            this._store.dispatch(new RefreshTokenLocalSuccess(
              auth.accessToken,
              auth.refreshToken,
              auth.expiresIn,
              auth.expiresAt
            ));
          }),
          finalize(() => { this._inFlight$ = undefined; }),
          shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    return this._inFlight$;
  }

  private _isTokenValid(expiresAt: string): boolean {
    if (!expiresAt) { return false; }

    const expiresMs = new Date(expiresAt).getTime();

    if (isNaN(expiresMs)) { return false; }

    return expiresMs - Date.now() > REFRESH_BUFFER_MS;
  }
}
